/**
 * Enter e Tab pulam de célula nas fichas (onda 354 · RN-275).
 *
 * «Implementasse o pulo para a próxima célula usando Enter e Tab, assim é
 * possível preencher de forma mais rápida e otimizada» (Humberto,
 * 23/10/2026).
 *
 * Um mecanismo só, para todas as tabelas de lançamento: o `onKeyDown` fica
 * na TABELA (ou na lista de cartões do celular, ou numa grade como o
 * «Pesar» da Estufa) e não em cada campo. A tabela se marca com
 * `data-teclado`; a `TabelaDeLancamento` já vem marcada, e uma tabela
 * escrita à mão espalha `{...tecladoDaTabela()}`.
 *
 * - **Enter** e **Tab** levam à próxima célula editável, na ordem de
 *   leitura (linha a linha). Botões, campos travados (`disabled`,
 *   `readOnly`), escondidos ou fora da tabulação (`tabIndex = -1`) não são
 *   célula. **Shift+Enter** e **Shift+Tab** voltam.
 * - **Tab na última célula da tabela** segue o navegador: sai da tabela
 *   (o foco não fica preso).
 * - **Enter na última célula editável de uma linha** grava a linha: clica
 *   o botão da linha marcado `data-gravar-linha` (o «Gravar ponto», o
 *   «Gravar determinação», o «Pesar»). Na tabela que grava tudo de uma vez
 *   («Gravar cápsulas», «Gravar peneiramento»), o botão marcado
 *   `data-gravar-tabela` — o primeiro depois da tabela — grava a partir da
 *   última linha editável. Gravou, o cursor vai para a primeira célula da
 *   linha seguinte (a linha nova em branco, onde a tela funciona assim).
 *   Não gravou (falta algo, o servidor recusou), o cursor fica onde está.
 * - **Na lista da `EscolhaPorCodigo`** o Enter continua escolhendo a
 *   opção — e, escolhida, segue para a célula seguinte.
 * - Ao entrar numa célula de texto, o conteúdo vem selecionado: digitar
 *   já substitui.
 * - No celular (RN-251), o teclado mostra «Próximo» (`enterkeyhint=next`)
 *   e, na célula que grava, «Concluído» (`done`).
 */
import type { FocusEvent, KeyboardEvent } from 'react';

type Celula = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** Tipos de `<input>` que não são célula de lançamento. */
const NAO_E_CELULA = new Set(['hidden', 'button', 'submit', 'reset', 'image', 'file']);

/** Tipos em que «selecionar o conteúdo» faz sentido. */
const SELECIONAVEL = new Set(['text', 'search', 'tel', 'url', 'email', 'number', 'password', '']);

const REGIAO = '[data-teclado]';

/** As props que fazem de uma tabela escrita à mão uma tabela de lançamento. */
export function tecladoDaTabela(nome = '') {
  return {
    'data-teclado': nome,
    onKeyDown: aoTeclarNaTabela,
    onFocus: aoFocarNaTabela,
  };
}

function ehCelula(el: Element | null): el is Celula {
  if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement
    || el instanceof HTMLTextAreaElement)) return false;
  /* `:disabled` também pega o campo de um `<fieldset disabled>` (ficha congelada). */
  if (el.matches(':disabled')) return false;
  if (!(el instanceof HTMLSelectElement) && el.readOnly) return false;
  if (el instanceof HTMLInputElement && NAO_E_CELULA.has(el.type)) return false;
  if (el.tabIndex < 0) return false;
  if (el.closest('[data-teclado-fora], [hidden], [aria-hidden="true"], [inert]')) return false;
  return el.getClientRects().length > 0;
}

/** As linhas da região: as do `<tbody>`, os cartões, ou a própria região. */
function linhasDe(regiao: HTMLElement): HTMLElement[] {
  const daqui = (l: Element) => l.closest(REGIAO) === regiao;
  let linhas: HTMLElement[];
  if (regiao instanceof HTMLTableElement) {
    linhas = Array.from(regiao.tBodies).flatMap((b) => Array.from(b.rows));
  } else if (regiao.tagName === 'UL' || regiao.tagName === 'OL') {
    linhas = Array.from(regiao.children).filter((c): c is HTMLElement => c.tagName === 'LI');
  } else {
    linhas = Array.from(regiao.querySelectorAll<HTMLElement>('[data-linha-teclado], tbody > tr'));
  }
  linhas = linhas.filter(daqui);
  return linhas.length ? linhas : [regiao];
}

function celulasDa(linha: HTMLElement, regiao: HTMLElement): Celula[] {
  return Array.from(linha.querySelectorAll('input, select, textarea'))
    .filter((el): el is Celula => ehCelula(el) && el.closest(REGIAO) === regiao);
}

interface Mapa { linhas: HTMLElement[]; celulas: { el: Celula; linha: number }[] }

function mapaDa(regiao: HTMLElement): Mapa {
  const linhas = linhasDe(regiao);
  const celulas = linhas.flatMap((l, i) => celulasDa(l, regiao).map((el) => ({ el, linha: i })));
  return { linhas, celulas };
}

/** O botão que grava a linha `i`, se Enter na última célula dela grava. */
function gravarDa(regiao: HTMLElement, mapa: Mapa, i: number): HTMLElement | null {
  const linha = mapa.linhas[i];
  const proprio = linha.querySelector<HTMLElement>('[data-gravar-linha]');
  if (proprio && proprio.closest(REGIAO) === regiao) return proprio;
  /* O «Gravar» da tabela inteira vale só a partir da última linha editável. */
  const ultimaEditavel = mapa.celulas[mapa.celulas.length - 1]?.linha;
  if (ultimaEditavel !== i) return null;
  return gravarDaTabela(regiao);
}

/**
 * O `data-gravar-tabela` da região: o primeiro depois dela na página, antes
 * da próxima tabela de lançamento (que teria o seu).
 */
function gravarDaTabela(regiao: HTMLElement): HTMLElement | null {
  const todos = Array.from(document.querySelectorAll<HTMLElement>(`${REGIAO}, [data-gravar-tabela]`));
  const k = todos.indexOf(regiao);
  if (k < 0) return null;
  for (const el of todos.slice(k + 1)) {
    if (el.hasAttribute('data-gravar-tabela')) {
      const dona = el.closest(REGIAO);
      if (!dona || dona === regiao) return el;
      continue;
    }
    if (!regiao.contains(el)) return null;
  }
  return null;
}

function focar(el: Celula) {
  el.focus();
  if (el instanceof HTMLTextAreaElement
    || (el instanceof HTMLInputElement && SELECIONAVEL.has(el.type))) {
    try { el.select(); } catch { /* o tipo não seleciona */ }
  }
}

function dica(el: Celula, valor: string) {
  if (el.getAttribute('enterkeyhint') !== valor) el.setAttribute('enterkeyhint', valor);
}

/** «Próximo» na célula que passa adiante; «Concluído» na que grava ou fecha. */
function ajustarDica(regiao: HTMLElement, el: Celula, mapa = mapaDa(regiao)) {
  const i = mapa.celulas.findIndex((c) => c.el === el);
  if (i < 0) return;
  const ultimaDaLinha = i === mapa.celulas.length - 1
    || mapa.celulas[i + 1].linha !== mapa.celulas[i].linha;
  const grava = ultimaDaLinha && gravarDa(regiao, mapa, mapa.celulas[i].linha) !== null;
  dica(el, grava || i === mapa.celulas.length - 1 ? 'done' : 'next');
}

export function aoFocarNaTabela(e: FocusEvent<HTMLElement>) {
  const regiao = e.currentTarget;
  const alvo = e.target as Element;
  if (alvo.closest(REGIAO) !== regiao || !ehCelula(alvo)) return;
  ajustarDica(regiao, alvo);
}

/** A lista da `EscolhaPorCodigo` aberta e sem opção: o código digitado não existe. */
function listaSemOpcao(el: Celula): boolean {
  if (el.getAttribute('role') !== 'combobox' || el.getAttribute('aria-expanded') !== 'true') return false;
  const lista = document.getElementById(el.getAttribute('aria-controls') ?? '');
  return !!lista && !lista.querySelector('[role="option"]');
}

export function aoTeclarNaTabela(e: KeyboardEvent<HTMLElement>) {
  const regiao = e.currentTarget;
  const alvo = e.target as Element;
  if (alvo.closest(REGIAO) !== regiao) return;
  const enter = e.key === 'Enter';
  if (!enter && e.key !== 'Tab') return;
  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
  if (!ehCelula(alvo)) return;
  if (enter && alvo instanceof HTMLTextAreaElement) return;
  const combobox = alvo.getAttribute('role') === 'combobox';
  if (e.defaultPrevented) {
    /* Só o Enter que escolheu uma opção da lista segue adiante. */
    if (!(enter && combobox)) return;
  } else if (enter && listaSemOpcao(alvo)) {
    return;
  }

  const mapa = mapaDa(regiao);
  const i = mapa.celulas.findIndex((c) => c.el === alvo);
  if (i < 0) return;

  if (e.shiftKey) {
    if (i === 0) return;
    e.preventDefault();
    focar(mapa.celulas[i - 1].el);
    return;
  }

  const linha = mapa.celulas[i].linha;
  const ultimaDaLinha = i === mapa.celulas.length - 1 || mapa.celulas[i + 1].linha !== linha;
  if (enter && ultimaDaLinha) {
    const botao = gravarDa(regiao, mapa, linha);
    if (botao) {
      e.preventDefault();
      gravarEPular(regiao, mapa, linha, botao);
      return;
    }
  }
  if (i < mapa.celulas.length - 1) {
    e.preventDefault();
    focar(mapa.celulas[i + 1].el);
  }
  /* Tab na última célula: o navegador leva para fora da tabela. */
}

// ── Gravar e pular para a linha seguinte ─────────────────────────────

/** Até quando se espera a gravação (a rede lenta da obra incluída). */
const ESPERA_MS = 15000;
const PASSO_MS = 50;

/** O que a região guarda para reencontrar-se depois de uma recarga. */
function regiaoViva(regiao: HTMLElement): HTMLElement | null {
  if (regiao.isConnected) return regiao;
  const nome = regiao.getAttribute('data-teclado');
  if (!nome) return null;
  return Array.from(document.querySelectorAll<HTMLElement>(REGIAO))
    .find((r) => r.getAttribute('data-teclado') === nome) ?? null;
}

/**
 * Clica o «Gravar» e espera. A gravação terminou quando a linha mudou: um
 * campo dela sumiu (a linha travou, RN-236) ou foi esvaziado (a linha nova
 * voltou em branco). Enquanto nada muda — está gravando, faltou algo, o
 * servidor recusou — o cursor fica. Quem tecla ou clica nesse meio tempo
 * assume: a espera desiste.
 */
function gravarEPular(regiao: HTMLElement, mapa: Mapa, linha: number, botao: HTMLElement) {
  const foto = mapa.celulas.filter((c) => c.linha === linha).map((c) => ({ el: c.el, valor: c.el.value }));
  const antes = mapa.linhas.length;
  const linhaEl = mapa.linhas[linha];
  let desistiu = false;
  const desistir = () => { desistiu = true; };
  /* O clique vai depois do render: a opção escolhida no Enter já está no estado. */
  window.setTimeout(() => {
    document.addEventListener('keydown', desistir, true);
    document.addEventListener('pointerdown', desistir, true);
    botao.click();
  }, 0);

  const fim = Date.now() + ESPERA_MS;
  let assinatura = '';
  const parar = () => {
    document.removeEventListener('keydown', desistir, true);
    document.removeEventListener('pointerdown', desistir, true);
  };
  const passo = () => {
    if (desistiu || Date.now() > fim) { parar(); return; }
    const ativo = document.activeElement;
    const nosso = !ativo || ativo === document.body || ativo === botao
      || foto.some((f) => f.el === ativo);
    if (!nosso) { parar(); return; }
    const mudou = !linhaEl.isConnected
      || foto.some((f) => !f.el.isConnected || f.el.value !== f.valor);
    const r = regiaoViva(regiao);
    if (!mudou || !r) { window.setTimeout(passo, PASSO_MS); return; }
    /* Espera a tela assentar (a recarga depois da gravação). */
    const m = mapaDa(r);
    const agora = `${m.linhas.length}:${m.celulas.length}`;
    if (agora !== assinatura) { assinatura = agora; window.setTimeout(passo, PASSO_MS); return; }
    /*
     * A linha seguinte. Se a linha gravada saiu da tabela (a tabela
     * encolheu), a seguinte ocupou o lugar dela; senão, é a de baixo — a
     * gravada continua ali, travada, e a nova em branco vem depois.
     */
    const inicio = !linhaEl.isConnected && m.linhas.length < antes ? linha : linha + 1;
    const prox = m.celulas.find((c) => c.linha >= inicio);
    /* A linha esvaziou antes de a recarga trazer a gravada: espera mais. */
    if (!prox) { window.setTimeout(passo, PASSO_MS); return; }
    parar();
    ajustarDica(r, prox.el, m);
    focar(prox.el);
  };
  window.setTimeout(passo, PASSO_MS);
}
